import { apiClient } from './api'
import type { DadosCadastraisDoCliente } from './resellerCustomerOrgApi'

export type Organization = {
  id: string
  name: string
  type: 'system' | 'cloud_provider' | 'reseller' | 'end_customer'
  description?: string
  frontend_url?: string
  gateway_saas_url?: string
  status: 'active' | 'suspended' | 'inactive'
  created_at: string
  updated_at: string
  users_count?: number
  settings?: Record<string, any>
  parent_id?: string
  /** Contrato tipado dos dados cadastrais (#1014), pelo leitor único do backend — `GET /organizations/{id}`. */
  dados_cadastrais?: DadosCadastraisDoCliente | null
}

export type CreateOrganizationDto = {
  name: string
  type: 'system' | 'cloud_provider' | 'reseller' | 'end_customer'
  description?: string
  frontend_url?: string
  gateway_saas_url?: string
  parent_id?: string
  settings?: Record<string, any>
}

export type UpdateOrganizationDto = {
  name?: string
  /** `null` limpa o campo; chave ausente mantém o valor gravado. */
  description?: string | null
  frontend_url?: string | null
  gateway_saas_url?: string | null
  // Sem `status` (#1122): o PUT só conhece `is_active` e descartava o campo calado.
  /** `null` desvincula do pai (torna o cliente livre). */
  parent_id?: string | null
  settings?: Record<string, any>
}

export const CHILDREN_PAGE_SIZE = 100
export const MEMBERS_PAGE_SIZE = 100

/** Vínculo de um usuário com a org (#1122): ativo OU suspenso, com os papéis dele ali. */
export type OrganizationMember = {
  membership_id: string
  user_id: string
  email: string | null
  full_name: string | null
  /** A CONTA (global); o vínculo pode estar ativo com a conta desativada. */
  user_is_active: boolean
  status: 'active' | 'suspended'
  is_default: boolean
  joined_at: string | null
  roles: { id: string; name: string }[]
}

export type OrganizationStats = {
  users_total: number
  users_active: number
  users_inactive: number
  child_organizations: number
  created_at: string
  type: string
  is_active: boolean
}

export const organizationApi = {
  // Listar todas as organizações
  async getAll(): Promise<Organization[]> {
    return apiClient.get<Organization[]>('/api/v1/organizations/')
  },

  // Buscar organizações por termo
  async searchOrganizations(params: { search: string; limit?: number }): Promise<Organization[]> {
    const searchParams = new URLSearchParams()
    if (params.search) searchParams.append('search', params.search)
    if (params.limit) searchParams.append('limit', params.limit.toString())

    return apiClient.get<Organization[]>(`/api/v1/organizations/?${searchParams}`)
  },

  // Buscar organização por ID
  async getById(id: string): Promise<Organization> {
    return apiClient.get<Organization>(`/api/v1/organizations/${id}`)
  },

  // Criar nova organização
  async create(data: CreateOrganizationDto): Promise<Organization> {
    return apiClient.post<Organization>('/api/v1/organizations/', data)
  },

  // Atualizar organização
  async update(id: string, data: UpdateOrganizationDto): Promise<Organization> {
    return apiClient.put<Organization>(`/api/v1/organizations/${id}`, data)
  },

  // Deletar organização
  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`/api/v1/organizations/${id}`)
  },

  // Obter estatísticas da organização
  async getStats(id: string): Promise<OrganizationStats> {
    return apiClient.get<OrganizationStats>(`/api/v1/organizations/${id}/stats`)
  },

  // Vínculos da org — ativos e suspensos (só superadmin; #1122)
  async getMembers(
    id: string,
    page: { skip: number; limit: number } = { skip: 0, limit: MEMBERS_PAGE_SIZE },
  ): Promise<OrganizationMember[]> {
    const params = new URLSearchParams({ skip: String(page.skip), limit: String(page.limit) })
    return apiClient.get<OrganizationMember[]>(`/api/v1/organizations/${id}/members?${params}`)
  },

  // Todos os vínculos: o endpoint pagina, como o das filhas.
  async getAllMembers(id: string): Promise<OrganizationMember[]> {
    const todos: OrganizationMember[] = []
    for (let skip = 0; ; skip += MEMBERS_PAGE_SIZE) {
      const pagina = await this.getMembers(id, { skip, limit: MEMBERS_PAGE_SIZE })
      todos.push(...pagina)
      if (pagina.length < MEMBERS_PAGE_SIZE) return todos
    }
  },

  // Alias para compatibilidade
  getOrganizations(): Promise<Organization[]> {
    return this.getAll()
  },

  // Obter usuários da organização
  async getUsers(id: string): Promise<any[]> {
    return apiClient.get<any[]>(`/api/v1/organizations/${id}/users`)
  },

  // Obter organizações filhas (parceiros/resellers)
  async getChildren(
    id: string,
    typeFilter?: string,
    page?: { skip: number; limit: number },
  ): Promise<Organization[]> {
    const params = new URLSearchParams()
    if (typeFilter) {
      params.append('type_filter', typeFilter)
    }
    if (page) {
      params.append('skip', String(page.skip))
      params.append('limit', String(page.limit))
    }

    return apiClient.get<Organization[]>(`/api/v1/organizations/${id}/children?${params}`)
  },

  // Todas as filhas: o endpoint pagina (limit=100 por padrão), e tratar a primeira página
  // como a hierarquia inteira escondia as seguintes (#1122).
  async getAllChildren(id: string, typeFilter?: string): Promise<Organization[]> {
    const todas: Organization[] = []
    for (let skip = 0; ; skip += CHILDREN_PAGE_SIZE) {
      const pagina = await this.getChildren(id, typeFilter, { skip, limit: CHILDREN_PAGE_SIZE })
      todas.push(...pagina)
      if (pagina.length < CHILDREN_PAGE_SIZE) return todas
    }
  },

  // Obter resellers parceiros de um cloud provider
  async getResellers(cloudProviderId: string): Promise<Organization[]> {
    return this.getAllChildren(cloudProviderId, 'reseller')
  },
}
